import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Search, 
  Filter, 
  ChevronLeft, 
  ChevronRight,
  Eye,
  Calendar,
  User,
  Activity,
  AlertCircle,
  CheckCircle2,
  XCircle,
  RefreshCcw,
  Download,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { TableRoot, Table, THead, TR, TH, TBody, TD } from '../../components/UI/Table';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { AuditLog, AuditLogFilters } from '../../lib/sdk/index';
import { useAuthStore } from '../../stores/authStore';
import { toast } from 'react-toastify';
import { useAuditLogs, useAuditLogStats } from '../../hooks/useQueries';

const AuditLogs: React.FC = () => {
  const { user } = useAuthStore();
  const [filters, setFilters] = useState<AuditLogFilters>({
    page: 1,
    limit: 50
  });
  const [showFilters, setShowFilters] = useState(false);
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());

  // React Query hooks
  const { data: auditLogsResponse, isLoading: loading, refetch: refetchAuditLogs } = useAuditLogs(
    user?.role === 'SuperAdmin' ? filters : undefined,
    { enabled: user?.role === 'SuperAdmin' }
  );
  
  const endDate = filters.endDate || new Date().toISOString().split('T')[0];
  const startDate = filters.startDate || new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0];
  const { data: stats } = useAuditLogStats(
    startDate,
    endDate,
    { enabled: user?.role === 'SuperAdmin' }
  );

  const auditLogs = Array.isArray(auditLogsResponse?.data) ? auditLogsResponse.data : [];
  const paginationData = auditLogsResponse?.pagination || { page: 1, limit: 50, total: 0, pages: 0 };
  const pagination = {
    page: paginationData.page,
    limit: paginationData.limit,
    total: paginationData.total,
    pages: paginationData.pages || paginationData.totalPages || 0
  };

  const handleFilterChange = (key: keyof AuditLogFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };
  
  const handleRefresh = () => {
    refetchAuditLogs();
  };

  const toggleLogExpansion = (logId: string) => {
    setExpandedLogs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'create':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'update':
        return <RefreshCcw className="h-4 w-4 text-blue-500" />;
      case 'delete':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'login':
        return <Activity className="h-4 w-4 text-green-500" />;
      case 'logout':
        return <Activity className="h-4 w-4 text-gray-500" />;
      default:
        return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    if (status === 'success') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Success
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
        <AlertCircle className="h-3 w-3 mr-1" />
        Failure
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const clearFilters = () => {
    setFilters({ page: 1, limit: 50 });
  };

  if (user?.role !== 'SuperAdmin') {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Access Denied</h2>
          <p className="text-gray-600 dark:text-gray-400">Only SuperAdmin can access audit logs.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <FileText className="h-6 w-6" />
            Audit Logs
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Complete transaction history and system activity logs
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            leftIcon={<Filter className="h-4 w-4" />}
          >
            {showFilters ? 'Hide Filters' : 'Filters'}
          </Button>
          <Button
            variant="outline"
            onClick={handleRefresh}
            leftIcon={<RefreshCcw className="h-4 w-4" />}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Logs</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
              </div>
              <FileText className="h-8 w-8 text-gray-400" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Success</p>
                <p className="text-2xl font-bold text-green-600">
                  {stats.statusCounts.find((s: any) => s._id === 'success')?.count || 0}
                </p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-400" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Failures</p>
                <p className="text-2xl font-bold text-red-600">
                  {stats.statusCounts.find((s: any) => s._id === 'failure')?.count || 0}
                </p>
              </div>
              <AlertCircle className="h-8 w-8 text-red-400" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Date Range</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {new Date(stats.dateRange.startDate).toLocaleDateString()} - {new Date(stats.dateRange.endDate).toLocaleDateString()}
                </p>
              </div>
              <Calendar className="h-8 w-8 text-gray-400" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      {showFilters && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Action
              </label>
              <select
                value={filters.action || ''}
                onChange={(e) => handleFilterChange('action', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All Actions</option>
                <option value="update">Update</option>
                <option value="delete">Delete</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Entity
              </label>
              <select
                value={filters.entity || ''}
                onChange={(e) => handleFilterChange('entity', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All Entities</option>
                <option value="User">User</option>
                <option value="Donor">Donor</option>
                <option value="GuestDonor">Guest Donor</option>
                <option value="Donation">Donation</option>
                <option value="Expense">Expense</option>
                <option value="Region">Region</option>
                <option value="Office">Office</option>
                <option value="Controller">Controller</option>
                <option value="Account">Account</option>
                <option value="Transaction">Transaction</option>
                <option value="DonationType">Donation Type</option>
                <option value="ExpenseType">Expense Type</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                User Role
              </label>
              <select
                value={filters.userRole || ''}
                onChange={(e) => handleFilterChange('userRole', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All Roles</option>
                <option value="SuperAdmin">SuperAdmin</option>
                <option value="Admin">Admin</option>
                <option value="Cashier">Cashier</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                value={filters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All Status</option>
                <option value="success">Success</option>
                <option value="failure">Failure</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                User Email
              </label>
              <Input
                type="text"
                value={filters.userEmail || ''}
                onChange={(e) => handleFilterChange('userEmail', e.target.value || undefined)}
                placeholder="Search by email..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Start Date
              </label>
              <Input
                type="date"
                value={filters.startDate || ''}
                onChange={(e) => handleFilterChange('startDate', e.target.value || undefined)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                End Date
              </label>
              <Input
                type="date"
                value={filters.endDate || ''}
                onChange={(e) => handleFilterChange('endDate', e.target.value || undefined)}
              />
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={clearFilters} className="w-full">
                Clear Filters
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Audit Logs Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : auditLogs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-600 dark:text-gray-400">No audit logs found</p>
          </div>
        ) : (
          <>
            <TableRoot>
              <Table>
                <THead>
                  <TR>
                    <TH>Timestamp</TH>
                    <TH>User</TH>
                    <TH>Action</TH>
                    <TH>Entity</TH>
                    <TH>Status</TH>
                    <TH>IP Address</TH>
                    <TH>Details</TH>
                  </TR>
                </THead>
                <TBody>
                  {auditLogs.map((log) => (
                    <React.Fragment key={log._id}>
                      <TR className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <TD className="font-mono text-xs">
                          {formatDate(log.timestamp)}
                        </TD>
                        <TD>
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-900 dark:text-white">
                              {log.userName}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {log.userEmail}
                            </span>
                            <span className="text-xs text-gray-400 dark:text-gray-500">
                              {log.userRole}
                            </span>
                          </div>
                        </TD>
                        <TD>
                          <div className="flex items-center gap-2">
                            {getActionIcon(log.action)}
                            <span className="capitalize font-medium">{log.action}</span>
                            {log.action === 'update' && log.changes && log.changes.length > 0 && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                ({log.changes.length} field{log.changes.length !== 1 ? 's' : ''})
                              </span>
                            )}
                          </div>
                        </TD>
                        <TD>
                          <div className="flex flex-col">
                            <span className="font-medium">{log.entity}</span>
                            {log.entityId && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                                ID: {log.entityId.substring(0, 8)}...
                              </span>
                            )}
                          </div>
                        </TD>
                        <TD>{getStatusBadge(log.status)}</TD>
                        <TD className="font-mono text-xs">{log.ipAddress}</TD>
                        <TD>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleLogExpansion(log._id)}
                            leftIcon={expandedLogs.has(log._id) ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          >
                            {expandedLogs.has(log._id) ? 'Hide' : 'Show'}
                          </Button>
                        </TD>
                      </TR>
                      {expandedLogs.has(log._id) && (
                        <TR>
                          <TD colSpan={7} className="bg-gray-50 dark:bg-gray-900/50 p-4">
                            <div className="space-y-3">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Method</p>
                                  <p className="text-sm font-mono">{log.method}</p>
                                </div>
                                <div>
                                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Endpoint</p>
                                  <p className="text-sm font-mono break-all">{log.endpoint}</p>
                                </div>
                                {log.statusCode && (
                                  <div>
                                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Status Code</p>
                                    <p className="text-sm">{log.statusCode}</p>
                                  </div>
                                )}
                                {log.errorMessage && (
                                  <div>
                                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Error</p>
                                    <p className="text-sm text-red-600 dark:text-red-400">{log.errorMessage}</p>
                                  </div>
                                )}
                              </div>
                              {log.deviceInfo && (
                                <div>
                                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Device Info</p>
                                  <div className="grid grid-cols-2 gap-2 text-sm">
                                    {log.deviceInfo.pcName && <span>PC: {log.deviceInfo.pcName}</span>}
                                    {log.deviceInfo.pcId && <span>PC ID: {log.deviceInfo.pcId}</span>}
                                    {log.deviceInfo.platform && <span>Platform: {log.deviceInfo.platform}</span>}
                                  </div>
                                </div>
                              )}
                              {log.changes && log.changes.length > 0 && (
                                <div>
                                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">
                                    {log.action === 'update' ? 'Updated Fields' : 'Deleted Fields'}
                                  </p>
                                  <div className="space-y-2">
                                    {log.changes.map((change, idx) => (
                                      <div key={idx} className="text-sm border-l-2 border-blue-500 pl-3 py-1">
                                        <div className="font-medium text-gray-900 dark:text-white mb-1">
                                          {change.field}
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                          <div>
                                            <span className="text-gray-500 dark:text-gray-400">Previous:</span>
                                            <div className="mt-1 p-2 bg-red-50 dark:bg-red-900/20 rounded text-red-700 dark:text-red-300 break-words">
                                              {change.oldValue === null || change.oldValue === undefined 
                                                ? <span className="italic text-gray-400">(empty)</span>
                                                : typeof change.oldValue === 'object' 
                                                  ? JSON.stringify(change.oldValue, null, 2)
                                                  : String(change.oldValue)}
                                            </div>
                                          </div>
                                          <div>
                                            <span className="text-gray-500 dark:text-gray-400">New:</span>
                                            <div className="mt-1 p-2 bg-green-50 dark:bg-green-900/20 rounded text-green-700 dark:text-green-300 break-words">
                                              {change.newValue === null || change.newValue === undefined 
                                                ? <span className="italic text-gray-400">(deleted)</span>
                                                : typeof change.newValue === 'object' 
                                                  ? JSON.stringify(change.newValue, null, 2)
                                                  : String(change.newValue)}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {log.metadata && Object.keys(log.metadata).length > 0 && (
                                <div>
                                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Metadata</p>
                                  <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded overflow-auto">
                                    {JSON.stringify(log.metadata, null, 2)}
                                  </pre>
                                </div>
                              )}
                            </div>
                          </TD>
                        </TR>
                      )}
                    </React.Fragment>
                  ))}
                </TBody>
              </Table>
            </TableRoot>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                  {pagination.total} results
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    leftIcon={<ChevronLeft className="h-4 w-4" />}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.pages}
                    rightIcon={<ChevronRight className="h-4 w-4" />}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AuditLogs;
